import mockReleaseData from "../data/mockData/releasesData";
import mockReleaseDetailsData from "../data/mockData/releaseDetailsData";
import { getDetail, paginateRows } from "./listQueryUtils";
import { normalizeDemoArtworkField } from "./demoArtworkHelpers";

const STORAGE_KEY = "wl_bs_delivery_confirmations_by_release";
const RELEASE_LIST_KEY = "wl_bs_releases_extras";
const RELEASE_DETAILS_KEY = "wl_bs_release_details_by_id";

/** Major DSP store groups — almost always delivered on initial distribution. */
const CORE_STORES = ["8S", "20", "15", "17", "16", "19", "14", "10", "2", "1"];
/** Optional stores that may fail on metadata updates. */
const OPTIONAL_STORES = ["5", "7", "8J", "8D", "83086"];
/** Aggregated optional group (includes Yandex / UMA in breakdown UI). */
const AGGREGATED_OPTIONAL_STORE = "6";

const LIST_ACTIONS = ["INSERT", "FULL_UPDATE", "METADATA_UPDATE", "TAKEDOWN"];

function readJson(key, fallback) {
  if (typeof sessionStorage === "undefined") {
    return fallback;
  }
  try {
    const raw = sessionStorage.getItem(key);
    if (raw == null) {
      return fallback;
    }
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function writeJson(key, value) {
  if (typeof sessionStorage === "undefined") {
    return;
  }
  sessionStorage.setItem(key, JSON.stringify(value));
}

function mergeById(baseRows, extras) {
  const map = new Map();
  (baseRows || []).forEach(row => {
    if (row?.id != null) {
      map.set(String(row.id), row);
    }
  });
  (extras || []).forEach(row => {
    if (row?.id != null) {
      map.set(String(row.id), { ...map.get(String(row.id)), ...row });
    }
  });
  return Array.from(map.values());
}

function seededRandom(seed) {
  let state = Math.abs(Number(seed) || 1);
  return () => {
    state = (state * 1103515245 + 12345) & 0x7fffffff;
    return state / 0x7fffffff;
  };
}

function normalizeReleaseArtwork(artwork) {
  return normalizeDemoArtworkField(artwork);
}

function resolveReleaseArtwork(release) {
  if (!release?.id) {
    return null;
  }

  const listRows = mergeById(mockReleaseData, readJson(RELEASE_LIST_KEY, []));
  const listRow = getDetail(listRows, release.id);
  const seedDetail = getDetail(mockReleaseDetailsData, release.id);

  const candidates = [
    release.artwork,
    release.release_artwork,
    listRow?.artwork,
    seedDetail?.artwork,
  ];

  for (const candidate of candidates) {
    const normalized = normalizeReleaseArtwork(candidate);
    if (normalized) {
      return normalized;
    }
  }

  return null;
}

function enrichConfirmationRowFromRelease(row) {
  if (!row?.release) {
    return row;
  }

  const release = getMergedReleaseDetail(row.release);
  const releaseArtwork = resolveReleaseArtwork(release);

  return {
    ...row,
    release_name: row.release_name || release.name || "Release",
    upc: row.upc || release.ean || "",
    release_artwork: releaseArtwork,
  };
}

function getMergedReleaseDetail(releaseId) {
  const sid = String(releaseId);
  const listExtras = readJson(RELEASE_LIST_KEY, []);
  const details = readJson(RELEASE_DETAILS_KEY, {});
  const listRows = mergeById(mockReleaseData, listExtras);
  const listRow = getDetail(listRows, releaseId) || {};

  if (details[sid]) {
    return { ...listRow, ...details[sid], id: details[sid].id ?? releaseId };
  }

  const seedDetail = getDetail(mockReleaseDetailsData, releaseId);
  if (seedDetail) {
    return { ...seedDetail, ...listRow, id: seedDetail.id ?? releaseId };
  }

  return { ...listRow, id: Number(releaseId) || releaseId };
}

function readAllConfirmationsByRelease() {
  return readJson(STORAGE_KEY, {});
}

function writeAllConfirmationsByRelease(map) {
  writeJson(STORAGE_KEY, map || {});
}

function buildStoreConfirmations(storeIds, deliveredByStore) {
  return storeIds.map(store => ({
    store,
    status: deliveredByStore[store] !== false,
  }));
}

function deriveListStatus(storeConfirmations) {
  const rows = storeConfirmations || [];
  if (!rows.length) {
    return "UNDELIVERED";
  }
  const deliveredCount = rows.filter(row => row.status).length;
  if (deliveredCount === rows.length) {
    return "DELIVERED";
  }
  if (deliveredCount === 0) {
    return "UNDELIVERED";
  }
  return "PARTIALLY DELIVERED";
}

function daysAgo(days) {
  const dt = new Date();
  dt.setDate(dt.getDate() - days);
  dt.setHours(9 + (days % 6), 15, 0, 0);
  return dt.toISOString();
}

function buildConfirmationRow({
  id,
  release,
  action,
  storeIds,
  deliveredByStore,
  created,
}) {
  const store_confirmations = buildStoreConfirmations(storeIds, deliveredByStore);
  const releaseArtwork = resolveReleaseArtwork(release);

  return {
    id,
    release: release.id,
    release_name: release.name || "Release",
    upc: release.ean || "",
    action,
    status: deriveListStatus(store_confirmations),
    created,
    ...(releaseArtwork ? { release_artwork: releaseArtwork } : {}),
    store_confirmations,
  };
}

/** Build a realistic delivery history — always includes INSERT and FULL_UPDATE. */
export function buildDeliveryConfirmationsForRelease(release) {
  if (!release?.id) {
    return [];
  }

  const rand = seededRandom(Number(release.id) * 9973);
  const allStores = [...CORE_STORES, AGGREGATED_OPTIONAL_STORE];
  const optionalPick = OPTIONAL_STORES[Math.floor(rand() * OPTIONAL_STORES.length)];
  if (rand() > 0.35) {
    allStores.push(optionalPick);
  }

  const delivered = Object.fromEntries(allStores.map(store => [store, true]));
  const partialDelivered = { ...delivered, [optionalPick]: false };
  const undelivered = Object.fromEntries(allStores.map(store => [store, false]));

  const baseId = Number(release.id) * 1000;
  const rows = [
    buildConfirmationRow({
      id: baseId + 1,
      release,
      action: "INSERT",
      storeIds: allStores,
      deliveredByStore: delivered,
      created: daysAgo(18 + Math.floor(rand() * 4)),
    }),
    buildConfirmationRow({
      id: baseId + 2,
      release,
      action: "FULL_UPDATE",
      storeIds: allStores,
      deliveredByStore: delivered,
      created: daysAgo(9 + Math.floor(rand() * 3)),
    }),
  ];

  if (rand() > 0.25) {
    rows.push(
      buildConfirmationRow({
        id: baseId + 3,
        release,
        action: "METADATA_UPDATE",
        storeIds: allStores,
        deliveredByStore: partialDelivered,
        created: daysAgo(4 + Math.floor(rand() * 2)),
      }),
    );
  }

  if (rand() > 0.55) {
    rows.push(
      buildConfirmationRow({
        id: baseId + 4,
        release,
        action: "FULL_UPDATE",
        storeIds: allStores,
        deliveredByStore: delivered,
        created: daysAgo(1 + Math.floor(rand() * 2)),
      }),
    );
  }

  return rows.sort((a, b) => new Date(b.created) - new Date(a.created));
}

export function seedDeliveryConfirmationsForRelease(releaseId, releaseDetail) {
  const sid = String(releaseId);
  const all = readAllConfirmationsByRelease();
  if (Array.isArray(all[sid]) && all[sid].length) {
    return all[sid];
  }

  const release = releaseDetail || getMergedReleaseDetail(releaseId);
  const rows = buildDeliveryConfirmationsForRelease(release);
  all[sid] = rows;
  writeAllConfirmationsByRelease(all);
  return rows;
}

export function clearDeliveryConfirmationsForRelease(releaseId) {
  const sid = String(releaseId);
  const all = readAllConfirmationsByRelease();
  if (!all[sid]) {
    return;
  }
  delete all[sid];
  writeAllConfirmationsByRelease(all);
}

export function clearDeliveryConfirmationsStorage() {
  if (typeof sessionStorage === "undefined") {
    return;
  }
  sessionStorage.removeItem(STORAGE_KEY);
}

function ensureReleaseConfirmations(releaseId) {
  const sid = String(releaseId);
  const all = readAllConfirmationsByRelease();
  if (Array.isArray(all[sid]) && all[sid].length) {
    return all[sid];
  }

  const release = getMergedReleaseDetail(releaseId);
  const hasConfirmations =
    release.has_delivery_confirmations === true || release.status === "distributed";

  if (!hasConfirmations) {
    return [];
  }

  return seedDeliveryConfirmationsForRelease(releaseId, release);
}

function flattenConfirmations(releaseId) {
  if (releaseId != null && releaseId !== "") {
    return ensureReleaseConfirmations(releaseId);
  }

  const all = readAllConfirmationsByRelease();
  const merged = [];
  Object.keys(all).forEach(key => {
    const rows = ensureReleaseConfirmations(key);
    merged.push(...rows);
  });
  return merged;
}

function findConfirmationById(confirmationId) {
  const all = readAllConfirmationsByRelease();
  const sid = String(confirmationId);

  for (const releaseKey of Object.keys(all)) {
    const rows = ensureReleaseConfirmations(releaseKey);
    const match = rows.find(row => String(row.id) === sid);
    if (match) {
      return match;
    }
  }

  const releaseKeys = new Set([
    ...Object.keys(all),
    ...mergeById(mockReleaseData, readJson(RELEASE_LIST_KEY, [])).map(row => String(row.id)),
  ]);

  for (const releaseKey of releaseKeys) {
    const rows = buildDeliveryConfirmationsForRelease(getMergedReleaseDetail(releaseKey));
    const match = rows.find(row => String(row.id) === sid);
    if (match) {
      seedDeliveryConfirmationsForRelease(releaseKey);
      return match;
    }
  }

  return null;
}

export function resolveDeliveryConfirmationList(queryParams) {
  const releaseId = queryParams?.get?.("release") || queryParams?.release || null;
  const rows = flattenConfirmations(releaseId)
    .filter(row => LIST_ACTIONS.includes(row.action))
    .map(enrichConfirmationRowFromRelease);
  return paginateRows(rows, queryParams);
}

export function resolveDeliveryConfirmationDetail(confirmationId) {
  const row = findConfirmationById(confirmationId);
  if (!row) {
    return null;
  }

  const enriched = enrichConfirmationRowFromRelease(row);
  const { store_confirmations, ...listFields } = enriched;
  return {
    ...listFields,
    store_confirmations: store_confirmations || [],
  };
}

export function appendTakedownDeliveryConfirmation(releaseId, releaseDetail) {
  const sid = String(releaseId);
  const release = releaseDetail || getMergedReleaseDetail(releaseId);
  const existing = readAllConfirmationsByRelease()[sid] || buildDeliveryConfirmationsForRelease(release);
  const storeIds = existing[0]?.store_confirmations?.map(row => row.store) || CORE_STORES;
  const undelivered = Object.fromEntries(storeIds.map(store => [store, false]));
  const baseId = Number(release.id) * 1000 + existing.length + 1;

  const takedownRow = buildConfirmationRow({
    id: baseId,
    release,
    action: "TAKEDOWN",
    storeIds,
    deliveredByStore: undelivered,
    created: new Date().toISOString(),
  });

  const all = readAllConfirmationsByRelease();
  all[sid] = [takedownRow, ...existing];
  writeAllConfirmationsByRelease(all);
  return takedownRow;
}
